import React, { useState, useEffect, useRef, useCallback } from 'react';
import { DROPDOWN_ANIMATION, DROPDOWN_BG } from './ui/tokens';
import { useDropdownAnimation } from '../hooks/useDropdownAnimation';
import { Bell, Check, Eye, Trash2, X, Send, FileText, MessageSquareWarning, CheckCheck, Sparkles, Clock } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { nivelPanelBalance } from '../utils/permissions';
import { rutaAvisoBalance } from '../utils/rutaAvisoBalance';
import { entidadPropia, rutaPanelPendientes, rutaReemplazarComprobante } from '../utils/comprobanteEntidad';
import { notificacionesService } from '../services/notificacionesService';
import { notificacionBalanceService } from '../services/notificacionBalanceService';
import { Notificacion, supabase } from '../lib/supabase';
import { useAuth } from '../context/AuthContext';
import { ChangelogModal } from './changelog/ChangelogModal';

function tiempoTranscurrido(dateString: string): string {
  const diff = Date.now() - new Date(dateString).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'Ahora';
  if (mins < 60) return `Hace ${mins} min`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `Hace ${hours} h`;
  const days = Math.floor(hours / 24);
  return `Hace ${days} d`;
}

const TIPO_ICONS: Record<string, React.ReactNode> = {
  envio_pendiente:   <Send className="w-4 h-4 text-sky-600" />,
  envio_confirmado:  <Check className="w-4 h-4 text-emerald-600" />,
  envio_rechazado:   <X className="w-4 h-4 text-red-600" />,
  balance_enviado:   <FileText className="w-4 h-4 text-sky-600" />,
  balance_recibido:  <CheckCheck className="w-4 h-4 text-emerald-600" />,
  balance_observado: <MessageSquareWarning className="w-4 h-4 text-amber-500" />,
  nueva_version:      <Sparkles className="w-4 h-4 text-blue-600" />,
  version_pendiente:  <Clock className="w-4 h-4 text-amber-600" />,
};

const BALANCE_TIPOS = new Set(['balance_enviado', 'balance_recibido', 'balance_observado']);

export const NotificacionesBell: React.FC = () => {
  const { authUser, usuario } = useAuth();
  const navigate = useNavigate();
  const [notificaciones, setNotificaciones] = useState<Notificacion[]>([]);
  const [noLeidas, setNoLeidas] = useState(0);
  const dropdown = useDropdownAnimation();
  const [eliminando, setEliminando] = useState<Set<string>>(new Set());
  const [changelogOpen, setChangelogOpen] = useState(false);
  const [highlightedVersion, setHighlightedVersion] = useState<string | undefined>();
  const ref = useRef<HTMLDivElement>(null);

  const cargar = useCallback(async () => {
    if (!authUser) return;
    try {
      const [lista, count] = await Promise.all([
        notificacionesService.getByUsuario(authUser.id, 15),
        notificacionesService.contarNoLeidas(authUser.id),
      ]);
      setNotificaciones(lista);
      setNoLeidas(count);
    } catch (e) {
      console.error(e);
    }
  }, [authUser]);

  useEffect(() => {
    if (!authUser) return;
    cargar();

    const channel = supabase
      .channel(`notificaciones:${authUser.id}`)
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'notificaciones',
          filter: `usuario_id=eq.${authUser.id}`,
        },
        () => { cargar(); }
      )
      .subscribe();

    return () => { supabase.removeChannel(channel); };
  }, [authUser, cargar]);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        dropdown.close();
      }
    };
    if (dropdown.isMounted) document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [dropdown.isMounted, dropdown.close]);

  const marcarLeida = async (id: string) => {
    const prevNotificaciones = notificaciones;
    const prevNoLeidas = noLeidas;
    try {
      await notificacionesService.marcarLeida(id);
      setNotificaciones(prev => prev.map(n => n.id === id ? { ...n, leida: true } : n));
      setNoLeidas(prev => Math.max(0, prev - 1));
    } catch (err) {
      console.error('Error al marcar notificación como leída:', err);
      setNotificaciones(prevNotificaciones);
      setNoLeidas(prevNoLeidas);
    }
  };

  const eliminar = async (id: string, eraNoLeida: boolean) => {
    if (eliminando.has(id)) return;
    setEliminando(prev => new Set(prev).add(id));
    const prevNotificaciones = notificaciones;
    const prevNoLeidas = noLeidas;
    try {
      await notificacionesService.eliminar(id);
      setNotificaciones(prev => prev.filter(n => n.id !== id));
      if (eraNoLeida) setNoLeidas(prev => Math.max(0, prev - 1));
    } catch (err) {
      console.error('Error al eliminar notificación:', err);
      setNotificaciones(prevNotificaciones);
      setNoLeidas(prevNoLeidas);
    } finally {
      setEliminando(prev => { const next = new Set(prev); next.delete(id); return next; });
    }
  };

  const marcarTodasLeidas = async () => {
    if (!authUser) return;
    const prevNotificaciones = notificaciones;
    const prevNoLeidas = noLeidas;
    try {
      await notificacionesService.marcarTodasLeidas(authUser.id);
      setNotificaciones(prev => prev.map(n => ({ ...n, leida: true })));
      setNoLeidas(0);
    } catch (err) {
      console.error('Error al marcar todas las notificaciones como leídas:', err);
      setNotificaciones(prevNotificaciones);
      setNoLeidas(prevNoLeidas);
    }
  };

  const handleNotificacionClick = async (n: Notificacion) => {
    dropdown.close();
    if (!n.leida) void marcarLeida(n.id);

    if (n.tipo === 'nueva_version') {
      const match = /v(\d+\.\d+(?:\.\d+)?)/.exec(n.titulo);
      setHighlightedVersion(match ? match[1] : undefined);
      setChangelogOpen(true);
      return;
    }

    if (n.tipo === 'version_pendiente') {
      navigate('/admin/versiones');
      return;
    }

    if (n.tipo === 'comprobantes_regularizacion') {
      const entidad = entidadPropia(usuario);
      if (entidad) navigate(rutaPanelPendientes(entidad));
      return;
    }

    if (n.tipo === 'almacenamiento_70') return;

    if (!BALANCE_TIPOS.has(n.tipo) || !n.referencia_id) return;

    try {
      const notifBalance = await notificacionBalanceService.getById(n.referencia_id);
      if (!notifBalance) return;

      const { periodo_mes: mes, periodo_anio: anio } = notifBalance;
      navigate(rutaAvisoBalance(nivelPanelBalance(usuario), n.tipo, { mes, año: anio }));
    } catch {
      // Si falla el fetch, igual abrimos la página sin período
      navigate(rutaAvisoBalance(nivelPanelBalance(usuario), n.tipo));
    }
  };

  // El aviso con comprobantes objetados abre la carga del más antiguo (H23).
  const handleReemplazarComprobante = (n: Notificacion) => {
    dropdown.close();
    if (!n.leida) void marcarLeida(n.id);
    const entidad = entidadPropia(usuario);
    if (entidad) navigate(rutaReemplazarComprobante(entidad));
  };

  return (
    <div ref={ref} className="relative">
      <button
        onClick={dropdown.toggle}
        className="relative p-2 hover:bg-gray-100 rounded-lg transition-colors active:scale-[0.98]"
        aria-label="Notificaciones"
      >
        <Bell className="w-5 h-5 text-gray-600" />
        {noLeidas > 0 && (
          <span className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] bg-red-500 text-white text-xs font-bold rounded-full flex items-center justify-center px-1">
            {noLeidas > 99 ? '99+' : noLeidas}
          </span>
        )}
      </button>

      {dropdown.isMounted && (
        <div
          data-state={dropdown.dataState}
          className={`absolute right-0 top-full mt-2 w-80 rounded-xl shadow-xl z-50 overflow-hidden ${DROPDOWN_BG} ${DROPDOWN_ANIMATION}`}
          style={{ transformOrigin: 'top right' }}
          onAnimationEnd={dropdown.onAnimationEnd}
        >
          <div className="flex items-center justify-between px-4 py-3 border-b border-slate-100">
            <h3 className="font-bold text-slate-800 text-sm">Notificaciones</h3>
            {noLeidas > 0 && (
              <button
                onClick={marcarTodasLeidas}
                className="text-xs text-sky-600 hover:text-sky-700 font-medium"
              >
                Marcar todas como leídas
              </button>
            )}
          </div>

          {notificaciones.length === 0 ? (
            <div className="py-10 text-center">
              <Bell className="w-10 h-10 text-slate-200 mx-auto mb-2" />
              <p className="text-sm text-slate-400">Sin notificaciones</p>
            </div>
          ) : (
            <div className="max-h-96 overflow-y-auto divide-y divide-slate-100">
              {notificaciones.map(n => (
                <div
                  key={n.id}
                  className={`relative flex items-start gap-3 px-4 py-3 transition-colors ${
                    n.leida ? 'hover:bg-slate-50' : 'bg-sky-50 hover:bg-sky-100'
                  }`}
                >
                  {(BALANCE_TIPOS.has(n.tipo) || n.tipo === 'nueva_version' || n.tipo === 'version_pendiente'
                    || n.tipo === 'comprobantes_regularizacion') && (
                    <button
                      type="button"
                      aria-label={n.titulo}
                      className="absolute inset-0 z-0 cursor-pointer"
                      onClick={() => { handleNotificacionClick(n); }}
                    />
                  )}
                  <div className={`relative z-10 flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center pointer-events-none ${
                    n.leida ? 'bg-slate-100' : 'bg-sky-100'
                  }`}>
                    {TIPO_ICONS[n.tipo] || <Bell className="w-4 h-4 text-slate-500" />}
                  </div>
                  <div className="relative z-10 flex-1 min-w-0 pointer-events-none">
                    <p className={`text-sm leading-tight ${n.leida ? 'text-slate-600' : 'font-semibold text-slate-800'}`}>
                      {n.titulo}
                    </p>
                    {n.cuerpo && <p className="text-xs text-slate-500 mt-0.5 line-clamp-2">{n.cuerpo}</p>}
                    {n.tipo === 'comprobantes_regularizacion' && n.referencia_tipo === 'entidad_comprobantes_objetados' && (
                      <button
                        type="button"
                        onClick={(e) => { e.stopPropagation(); handleReemplazarComprobante(n); }}
                        className="pointer-events-auto mt-1 min-h-11 text-xs font-semibold text-sky-700 hover:text-sky-900"
                      >
                        Reemplazar comprobante
                      </button>
                    )}
                    <p className="text-xs text-slate-400 mt-1">{tiempoTranscurrido(n.created_at)}</p>
                  </div>
                  <div className="relative z-10 flex-shrink-0 flex items-center gap-0.5">
                    {!n.leida && (
                      <button
                        type="button"
                        onClick={(e) => { e.stopPropagation(); marcarLeida(n.id); }}
                        className="p-1 hover:bg-sky-200 rounded-full transition-colors"
                        aria-label="Marcar como leída"
                        title="Marcar como leída"
                      >
                        <Eye className="w-3 h-3 text-sky-600" />
                      </button>
                    )}
                    <button
                      type="button"
                      onClick={(e) => { e.stopPropagation(); eliminar(n.id, !n.leida); }}
                      className="p-1 hover:bg-slate-200 rounded-full transition-colors disabled:opacity-40"
                      aria-label="Descartar notificación"
                      title="Descartar notificación"
                      disabled={eliminando.has(n.id)}
                    >
                      <Trash2 className="w-3 h-3 text-slate-400" />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
      <ChangelogModal
        isOpen={changelogOpen}
        onClose={() => { setChangelogOpen(false); setHighlightedVersion(undefined); }}
        highlightVersionCode={highlightedVersion}
      />
    </div>
  );
};
